import { Tag } from '@carbon/react';
import type { WellPastVersion } from './types';
import styles from './past-versions-list.module.scss';

export interface PastVersionsListProps {
  /** The viewed well's name, so the subtitle names what these are versions of. */
  anchorName: string;
  pastVersions: WellPastVersion[];
}

/**
 * The records already reconciled into the viewed well.
 *
 * They are not part of the pool and cannot be acted on: a retired record is no longer a canonical
 * well, so there is nothing left to decide about it. The field-by-field duplicate-vs-survivor
 * comparison is not shown here — it lives on the reconcile workspace's "Compare to Promoted" sheet,
 * where a reconciled duplicate diffs cross-UWI against its survivor across every comparable attribute.
 * @param {PastVersionsListProps} props The viewed well's name and its retired records.
 * @return {JSX.Element | null} The list, or nothing when the well has absorbed none.
 */
export function PastVersionsList({
  anchorName,
  pastVersions,
}: PastVersionsListProps) {
  if (pastVersions.length === 0) return null;

  return (
    <section className={styles.block}>
      <div className={styles.head}>
        <h5 className={styles.title}>Existing past versions</h5>
        <Tag type="cool-gray" size="sm">
          {pastVersions.length}
        </Tag>
      </div>
      <p className={styles.subtitle}>Already reconciled into {anchorName}.</p>
      <ul className={styles.list}>
        {pastVersions.map((version) => (
          <li key={version.uwi} className={styles.row}>
            <span className={styles.rowText}>
              <span className={styles.name}>
                {version.wellName ?? version.uwi}
              </span>
              <span className={styles.meta}>UWI {version.uwi}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
